import { FC, useCallback, useEffect, useMemo, useState } from "react";

import { LineChart } from "../../../Charts/LineChart/LineChart";
import { useBreakpoint } from "../../../../hooks/useBreakpoint";

import { PatentsFiledAndGrantedForHomeAppliancesProps } from "./PatentsFiledAndGrantedForHomeAppliances.type";
import { PATENTS_FILED_AND_GRANTED_FOR_HOME_APPLIANCES_CHART_DATA_KEYS } from "./PatentsFiledAndGrantedForHomeAppliances.const";

const PatentsFiledAndGrantedForHomeAppliances: FC<PatentsFiledAndGrantedForHomeAppliancesProps> = ({ data }) => {
	const chartSize = useBreakpoint();

	const [chartData, setChartData] = useState(data);
	const [axisFontSize, setAxisFontSize] = useState(12);
	const [marginLeft, setMarginLeft] = useState(0);
	const [xAxisPadding, setXAxisPadding] = useState(50);
	const [chartHeight, setChartHeight] = useState(450);

	const dataKeys = useMemo(() => {
		return PATENTS_FILED_AND_GRANTED_FOR_HOME_APPLIANCES_CHART_DATA_KEYS.map((dataKey) => ({
			...dataKey,
			label: typeof dataKey.translation === "string" ? dataKey.translation : dataKey.name,
			labelAnchor: "first" as const,
		}));
	}, []);

	useEffect(() => {
		switch (chartSize) {
			case "xs":
				setChartData(data.filter((dataEntry) => ["2012", "2015", "2018", "2022", "2025"].includes(dataEntry.name)));
				setMarginLeft(0);
				setAxisFontSize(9);
				setXAxisPadding(20);
				setChartHeight(250);
				break;

			case "sm":
				setChartData(data.filter((dataEntry) => ["2013", "2015", "2017", "2019", "2021", "2023", "2025"].includes(dataEntry.name)));
				setMarginLeft(0);
				setAxisFontSize(11);
				setXAxisPadding(15);
				setChartHeight(350);
				break;

			case "md":
				setMarginLeft(0);
				setChartData(data);
				setAxisFontSize(12);
				setXAxisPadding(20);
				setChartHeight(350);
				break;

			case "lg":
			case "xl":
			default:
				setMarginLeft(0);
				setChartData(data);
				setAxisFontSize(14);
				setXAxisPadding(100);
				setChartHeight(450);
				break;
		}
	}, [chartSize, data]);

	const formatNumber = useCallback((number: number) => {
		return new Intl.NumberFormat("nl-BE", {
			minimumFractionDigits: 0,
			maximumFractionDigits: 0,
		}).format(number);
	}, []);

	return (
		<div className="flex flex-col">
			<h3 className="self-start text-black font-bold text-2xl mb-4">
				{"Patents filed and granted for home appliances"}
			</h3>
			<LineChart
				data={chartData}
				height={chartHeight}
				marginLeft={marginLeft}
				marginRight={0}
				labelProps={{ fontSize: axisFontSize, formatter: formatNumber }}
				XAxisProps={{
					dataKey: "name",
					padding: { left: xAxisPadding, right: xAxisPadding },
					tick: { fontSize: axisFontSize },
				}}
				xDomain={[2012,2025]}
				YAxisProps={{
					tickCount: 9,
					tick: { fontSize: axisFontSize },
					tickFormatter: formatNumber,
					domain: [0, 16000],
					interval: 0,
				}}
				dataKeys={dataKeys}
				showToolTip={false}
			/>
		</div>
	);
};

export { PatentsFiledAndGrantedForHomeAppliances };
